import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import { useAppDispatch } from '@/app/store/hooks';
import { clearStoredTheme } from '@/shared/hooks/useTheme';
import { tokenService } from '@/shared/services/auth/tokenService';
import { logout } from '../../common/store/authSlice';

/** What the sign-in page says to a user sent back to it after changing their own password. */
export const PASSWORD_CHANGED_NOTICE =
  'Your password has been changed. Please sign in with your new password.';

/**
 * Ends the local session after the signed-in user's OWN password was changed, and opens the sign-in page with
 * {@link PASSWORD_CHANGED_NOTICE}.
 *
 * <p>NB-552: the platform ends every session of a user whose password changes -- this one included -- so the
 * tokens held here are already dead. Clearing them now, rather than waiting for the next request's 401, takes the
 * user straight to sign in with the new password instead of through a "session expired" detour. Used by every
 * place a user can change their own password: the first-sign-in dialog, the profile menu's Change Password and
 * the Security tab on their own record. An administrator who changed somebody else's password keeps their session.
 */
export function useSignOutAfterPasswordChange(): () => void {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return useCallback(() => {
    tokenService.clearAll();
    clearStoredTheme();
    dispatch(logout({ deliberate: true }));
    void navigate(ROUTES.LOGIN, { replace: true, state: { notice: PASSWORD_CHANGED_NOTICE } });
  }, [dispatch, navigate]);
}
